/**
 * @file ContractDrawerHeaderView.tsx
 * @description Pure presentation for the contract drawer / mobile page
 *   header (SE-4347 AC-7). Renders the player avatar (with initials
 *   fallback), name, metadata line ({flag} {tier} · {age}), and a
 *   Profile link with external-link icon on the right. The `variant`
 *   prop swaps the left-side chrome:
 *
 *   - `drawer` — close X on the right, no breadcrumb on the left;
 *     used inside the desktop right-anchored Drawer shell.
 *   - `page` — back-chevron + "Contracts" label on the left, no close
 *     X; used at the top of the mobile full-page route
 *     (`/squad/contracts/[playerId]`).
 *
 *   Both variants render the avatar + name + metadata block + Profile
 *   link, so identity is always visible regardless of surface.
 * @module features/contracts/components/ContractDrawerHeader/ContractDrawerHeaderView
 */

import { ChevronLeft, ExternalLink, X } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Avatar, AvatarFallbackText } from "@/components/atoms/Avatar";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** Chrome-swap variant for the header. */
export type ContractDrawerHeaderVariant = "drawer" | "page";

/** Stable accessibility-state objects to avoid new-object-on-render allocations. */
const CLOSE_ENABLED: { disabled: boolean } = { disabled: false };
const CLOSE_DISABLED: { disabled: boolean } = { disabled: true };

/** Props for {@link ContractDrawerHeaderView}. */
export interface ContractDrawerHeaderViewProps {
  /** Which surface the header is rendered on. */
  readonly variant: ContractDrawerHeaderVariant;
  /** Display name of the player (e.g. "Bruno Fernandes"). */
  readonly playerName: string;
  /**
   * Pre-computed initials shown inside the Avatar fallback (e.g. "BF").
   * The container is responsible for deriving these so this view stays
   * pure.
   */
  readonly initials: string;
  /**
   * Metadata line below the name (e.g. "Portugal · First Team · CAM · 31 Years").
   * May be an empty string while identity is still resolving.
   */
  readonly metadataLine: string;
  /**
   * Handler for the drawer close X. Only invoked when `variant ===
   * "drawer"` because the close X is not rendered in page mode.
   */
  readonly onClosePress: () => void;
  /**
   * Handler for the mobile back-chevron. Only invoked when `variant
   * === "page"`.
   */
  readonly onBackPress: () => void;
  /** Handler for the Profile link (always visible). */
  readonly onProfilePress: () => void;
  /**
   * Whether the drawer is allowed to close right now. Gates the close
   * X affordance so unsaved-edit flows can block dismissal upstream.
   */
  readonly canClose: boolean;
  /**
   * Whether the Profile link is actionable. Disabled when the player
   * identity has not resolved yet.
   */
  readonly isProfileEnabled: boolean;
}

/**
 * Pure presentation for the drawer / page header.
 * @param props - View props
 * @param props.variant - Chrome swap: "drawer" shows close X, "page"
 *   shows back-chevron + Contracts label
 * @param props.playerName - Player display name
 * @param props.initials - Avatar fallback initials
 * @param props.metadataLine - Metadata line below the name
 * @param props.onClosePress - Close X handler (drawer variant only)
 * @param props.onBackPress - Back-chevron handler (page variant only)
 * @param props.onProfilePress - Profile link handler
 * @param props.canClose - Whether close is currently allowed
 * @param props.isProfileEnabled - Whether the Profile link is actionable
 * @returns The rendered header
 */
const ContractDrawerHeaderView = ({
  variant,
  playerName,
  initials,
  metadataLine,
  onClosePress,
  onBackPress,
  onProfilePress,
  canClose,
  isProfileEnabled,
}: ContractDrawerHeaderViewProps) => (
  <Box testID="contract-drawer-header">
    {variant === "page" ? (
      <Stack
        direction="horizontal"
        className="items-center justify-between border-b border-outline-default px-4 py-3"
      >
        <Pressable
          testID="contract-drawer-back-chevron"
          onPress={onBackPress}
          className="flex-row items-center gap-1"
        >
          <Icon
            as={ChevronLeft}
            size="sm"
            className="text-needs-semantic-primary-600"
          />
          <Text
            variant="body-strong"
            className="text-needs-semantic-primary-600"
          >
            Contracts
          </Text>
        </Pressable>
        <Pressable
          testID="contract-drawer-profile-link"
          onPress={onProfilePress}
          disabled={!isProfileEnabled}
          className="flex-row items-center gap-1"
        >
          <Icon as={ExternalLink} size="xs" className="text-content-muted" />
          <Text variant="body-strong" className="text-content-muted">
            Profile
          </Text>
        </Pressable>
      </Stack>
    ) : null}

    <Stack
      direction="horizontal"
      className="items-center justify-between gap-3 p-4"
    >
      <Stack
        direction="horizontal"
        className="min-w-0 flex-1 items-center gap-3"
      >
        <Avatar size="md">
          <AvatarFallbackText>{initials}</AvatarFallbackText>
        </Avatar>
        {/*
         * SE-4784 — `min-w-0` lets this VStack shrink below its
         * content's intrinsic width inside the parent flex row, which
         * is what makes `numberOfLines={1}` actually clip on RN web
         * (without it the text forces the column wider than the
         * viewport on small screens and the header reflows / wraps).
         * The pattern mirrors `AddToListPlayerRowView` (SE-4147).
         */}
        <Stack className="min-w-0 flex-1">
          {/*
           * Wrap the Text nodes in `<Box testID=...>` so the e2e
           * regression spec (SE-4784) can pin the name and metadata
           * lines without reaching for class substrings — gluestack's
           * `Text` types don't surface `testID` directly. Mirrors the
           * `contract-drawer-tab-live_tracking-soon-badge` pattern in
           * `ContractDrawerTabsView`.
           */}
          <Box testID="contract-drawer-name">
            {/*
             * `isTruncated` flips on the gluestack `web:truncate`
             * variant (`overflow:hidden; text-overflow:ellipsis;
             * white-space:nowrap` on the rendered span) — the
             * gluestack default `whitespace-pre-wrap` class otherwise
             * shadows the inline styles RN-Web emits for
             * `numberOfLines`, so the metadata text wrapped to two
             * lines on 320px viewports despite `numberOfLines={1}`.
             * Both props are intentional: `isTruncated` covers web,
             * `numberOfLines={1}` covers native (iOS/Android).
             */}
            {/*
             * SE-7709: `w-full` states a width this label already had. It is a
             * direct child of `<Box testID="contract-drawer-name">` (a column,
             * items-stretch), inside the enclosing
             * `<Stack className="min-w-0 flex-1">` whose width is flex-derived.
             * RN never measures these glyphs intrinsically, so the Android
             * font-undercount clip cannot occur here. (Grep those two
             * ancestors rather than trusting a line number: ~L167/L158 at time
             * of writing. A shifted line is cosmetic; a missing `min-w-0
             * flex-1` Stack means this reasoning has expired.)
             */}
            <Text
              variant="title"
              className="w-full text-content-primary"
              isTruncated
              numberOfLines={1}
            >
              {playerName}
            </Text>
          </Box>
          {metadataLine !== "" ? (
            <Box testID="contract-drawer-metadata">
              <Text
                variant="body"
                className="text-content-muted"
                isTruncated
                numberOfLines={1}
              >
                {metadataLine}
              </Text>
            </Box>
          ) : null}
        </Stack>
      </Stack>

      {variant === "drawer" ? (
        <Stack direction="horizontal" className="items-center gap-2">
          <Pressable
            testID="contract-drawer-profile-link"
            onPress={onProfilePress}
            disabled={!isProfileEnabled}
            className="flex-row items-center gap-1 rounded-none px-2 py-1"
          >
            <Icon as={ExternalLink} size="xs" className="text-content-muted" />
            <Text variant="body-strong" className="text-content-muted">
              Profile
            </Text>
          </Pressable>
          <Pressable
            testID="contract-drawer-close"
            onPress={onClosePress}
            disabled={!canClose}
            accessibilityRole="button"
            accessibilityLabel="Close contract details"
            accessibilityState={canClose ? CLOSE_ENABLED : CLOSE_DISABLED}
            className="rounded-full p-2"
          >
            <Icon as={X} size="sm" className="text-content-primary" />
          </Pressable>
        </Stack>
      ) : null}
    </Stack>
  </Box>
);

ContractDrawerHeaderView.displayName = "ContractDrawerHeaderView";

export default memo(ContractDrawerHeaderView);
